// src/app/hours/components/hours/hours.component.ts
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';

import { HoursService } from '../services/hours.service';
import { RequiredNotificationsService } from '../../shared/notifications/notifications.service';
import {
  CurrentUserDto,
  DraftCell,
  IsoDate,
  UserWonDto,
  WeeklyReportAssignedSchedule,
  WeeklyReportBookedHourItem,
  WeeklyReportBookedHoursDay,
  WeeklyReportResponse,
  WonCardVm,
  YywwDto,
} from '../models/hours.model';

dayjs.extend(isoWeek);
dayjs.extend(utc);

type DayStatus =
  | 'approved'
  | 'match'
  | 'over-booked'
  | 'under-booked'
  | 'missing-punch'
  | 'paycode-holiday'
  | 'requested-offtime'
  | 'none';

interface PunchVm {
  id: number;
  hours?: number;
  totalHours?: number;
  clockIn: {
    id: number;
    logTime: string;
    timeZoneId?: string | null;
  } | null;
  clockOut: {
    id: number;
    logTime: string;
    timeZoneId?: string | null;
  } | null;
}

interface WeekDayVm {
  date: IsoDate;
  label: string;
  booked: number;
  clocked: number;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  breakTime: number;
  punchesTooltip: string;
  status: DayStatus;
  isToday: boolean;
  isSelected: boolean;
  timelogs: PunchVm[];
  isApproved: boolean;
  isLocked: boolean;
  lockReason: string | null;
}

interface HoursWonCardVm extends WonCardVm {
  selectedDayIsApproved: boolean;
  selectedDayIsLocked: boolean;
  selectedDayLockReason: string | null;
}

interface HoursVm {
  user: CurrentUserDto;
  week: YywwDto;
  days: WeekDayVm[];
  wonCards: HoursWonCardVm[];
  selectedDate: IsoDate;
  dirty: boolean;
  schedules: WeeklyReportAssignedSchedule[];
  dailyScheduled: (WeeklyReportAssignedSchedule | null)[];
  weeklyBookedTotal: number;
  weeklyClockedTotal: number;
  weeklyScheduledTotal: number;
  isPayrollSynced: boolean;
  payrollLockedMessage: string | null;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

@Component({
  selector: 'app-hours',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hours.component.html',
  styleUrls: ['./hours.component.scss'],
})
export class HoursComponent implements OnInit, OnDestroy {
  vm$: Observable<HoursVm> = of(this.safeEmptyVm());

  isLoading = true;
  errorMessage: string | null = null;

  weeks: YywwDto[] = [];
  currentWeekIndex = -1;
  isMobileView = false;

  private readonly mobileBreakpoint = 820;

  private user!: CurrentUserDto;
  private report!: WeeklyReportResponse;
  private userWons: UserWonDto[] = [];

  selectedDate!: IsoDate;

  private readonly draft = new Map<string, DraftCell>();
  private baseline = new Map<string, { bookedHourId?: number; hours: number }>();

  private weekBarResizeObserver?: ResizeObserver;
  private observedWeekBarShell?: HTMLElement;

  @ViewChild('weekBarShell')
  set weekBarShellRef(ref: ElementRef<HTMLElement> | undefined) {
    if (!ref?.nativeElement) return;
    this.observeWeekBarShell(ref.nativeElement);
  }

  constructor(
    private readonly hoursService: HoursService,
    private readonly cdr: ChangeDetectorRef,
    private readonly requiredNotificationsService: RequiredNotificationsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.updateViewportMode();
    this.requiredNotificationsService.initializeForCurrentWeek().subscribe();
    this.loadInitial();
  }

  ngOnDestroy(): void {
    this.weekBarResizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewportMode();
    this.syncWeekBarHeight();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isDirty) return;
    event.preventDefault();
    event.returnValue = '';
  }

  get isDirty(): boolean {
    return this.computeDirty();
  }

  goPrevious(): void {
    if (this.isMobileView) {
      this.prevDay();
      return;
    }

    this.prevWeek();
  }

  goNext(): void {
    if (this.isMobileView) {
      this.nextDay();
      return;
    }

    this.nextWeek();
  }

  canGoPrevious(): boolean {
    return this.isMobileView ? this.canGoPrevDay() : this.currentWeekIndex > 0;
  }

  canGoNext(): boolean {
    return this.isMobileView
      ? this.canGoNextDay()
      : this.currentWeekIndex >= 0 && this.currentWeekIndex < this.weeks.length - 1;
  }

  prevWeek(): void {
    if (this.isLoading || this.currentWeekIndex <= 0 || !this.confirmDiscardIfDirty()) return;

    this.currentWeekIndex -= 1;
    this.selectedDate = this.getDefaultSelectedDate(this.weeks[this.currentWeekIndex]);
    this.syncWeekToUrl(this.weeks[this.currentWeekIndex]?.fullYearWeek);
    this.reloadWeek();
  }

  nextWeek(): void {
    if (
      this.isLoading ||
      this.currentWeekIndex >= this.weeks.length - 1 ||
      !this.confirmDiscardIfDirty()
    ) {
      return;
    }

    this.currentWeekIndex += 1;
    this.selectedDate = this.getDefaultSelectedDate(this.weeks[this.currentWeekIndex]);
    this.syncWeekToUrl(this.weeks[this.currentWeekIndex]?.fullYearWeek);
    this.reloadWeek();
  }

  prevDay(): void {
    if (this.isLoading) return;

    const dayIndex = this.getSelectedDayIndexInCurrentWeek();

    if (dayIndex > 0) {
      this.selectedDate = this.getDateForWeekDayIndex(
        this.weeks[this.currentWeekIndex],
        dayIndex - 1,
      );
      this.renderVm();
      return;
    }

    if (this.currentWeekIndex <= 0 || !this.confirmDiscardIfDirty()) return;

    this.currentWeekIndex -= 1;
    this.selectedDate = this.getDateForWeekDayIndex(this.weeks[this.currentWeekIndex], 6);
    this.syncWeekToUrl(this.weeks[this.currentWeekIndex]?.fullYearWeek);
    this.reloadWeek();
  }

  nextDay(): void {
    if (this.isLoading) return;

    const dayIndex = this.getSelectedDayIndexInCurrentWeek();

    if (dayIndex < 6) {
      this.selectedDate = this.getDateForWeekDayIndex(
        this.weeks[this.currentWeekIndex],
        dayIndex + 1,
      );
      this.renderVm();
      return;
    }

    if (this.currentWeekIndex >= this.weeks.length - 1 || !this.confirmDiscardIfDirty()) return;

    this.currentWeekIndex += 1;
    this.selectedDate = this.getDateForWeekDayIndex(this.weeks[this.currentWeekIndex], 0);
    this.syncWeekToUrl(this.weeks[this.currentWeekIndex]?.fullYearWeek);
    this.reloadWeek();
  }

  canGoPrevDay(): boolean {
    if (this.currentWeekIndex < 0 || !this.weeks.length) return false;

    const dayIndex = this.getSelectedDayIndexInCurrentWeek();

    return dayIndex > 0 || this.currentWeekIndex > 0;
  }

  canGoNextDay(): boolean {
    if (this.currentWeekIndex < 0 || !this.weeks.length) return false;

    const dayIndex = this.getSelectedDayIndexInCurrentWeek();

    return dayIndex < 6 || this.currentWeekIndex < this.weeks.length - 1;
  }

  selectDay(date: IsoDate): void {
    if (this.selectedDate === date) return;

    this.selectedDate = date;
    this.renderVm();
  }

  getMobileVisibleDays(vm: HoursVm): WeekDayVm[] {
    if (!this.isMobileView) return vm.days;

    const selected = vm.days.find((day) => day.date === vm.selectedDate);

    return selected ? [selected] : vm.days.slice(0, 1);
  }

  addHours(workOrderNumberId: number, delta: number): void {
    if (delta <= 0 || !this.canEditSelectedWon(workOrderNumberId)) return;

    this.applyDelta(workOrderNumberId, delta);
  }

  subtractHours(workOrderNumberId: number, delta: number): void {
    if (delta <= 0 || !this.canEditSelectedWon(workOrderNumberId)) return;

    this.applyDelta(workOrderNumberId, -delta);
  }

  cancelChanges(): void {
    if (!this.isDirty) return;

    this.draft.clear();
    this.renderVm();
  }

  save(): void {
    if (this.isLoading || !this.isDirty || this.isCurrentWeekPayrollSynced()) return;

    const fullYearWeek = this.weeks[this.currentWeekIndex]?.fullYearWeek;

    if (!fullYearWeek) {
      this.errorMessage = 'Invalid week selection.';
      return;
    }

    const changed = Array.from(this.draft.values()).filter(
      (cell) => this.isCellChanged(cell) && this.canEditWonDate(cell.workOrderNumberId, cell.date),
    );

    if (!changed.length) {
      this.draft.clear();
      this.renderVm();
      return;
    }

    this.startLoading();
    this.errorMessage = null;

    this.vm$ = forkJoin(
      changed.map((cell) => this.hoursService.saveDraftCell(this.user.id, cell)),
    ).pipe(
      switchMap(() => this.loadWeekReport(fullYearWeek)),
      tap(() => this.draft.clear()),
      map(() => this.buildVm()),
      catchError((err) => {
        this.errorMessage = this.extractApiError(err) ?? 'Failed to save hours';
        return of(this.buildVm());
      }),
      finalize(() => {
        this.stopLoading();
      }),
    );
  }

  canDeactivate(): boolean {
    return !this.isDirty || confirm('You have unsaved changes. Leave without saving?');
  }

  formatBottomDate(date: IsoDate): string {
    return dayjs(date).format('MMM D');
  }

  getSelectedDayLabel(date: IsoDate): string {
    return dayjs(date).format('ddd • MMM D');
  }

  getDayLabel(dayIndex: number): string {
    return DAY_LABELS[dayIndex] ?? '';
  }

  getQuickFillDelta(vm: HoursVm): number {
    const day = this.getSelectedDayVm(vm);
    if (!day) return 0;

    const delta = (Number(day.clocked) || 0) - (Number(day.booked) || 0);

    return this.round2(Math.max(0, delta));
  }

  showQuickFill(vm: HoursVm): boolean {
    const day = this.getSelectedDayVm(vm);

    return !!day && (Number(day.clocked) || 0) > 0 && this.getQuickFillDelta(vm) > 0.0001;
  }

  quickFillToWon(vm: HoursVm, wonId: number): void {
    const delta = this.getQuickFillDelta(vm);

    if (delta <= 0 || !this.canEditSelectedWon(wonId)) return;

    this.applyDelta(wonId, delta);
  }

  clearDayForWon(wonId: number): void {
    if (!this.canEditSelectedWon(wonId)) return;

    const base = this.getEffectiveCell(wonId, this.selectedDate);
    const current = this.round2(Number(base.hours) || 0);

    if (current <= 0.0001) return;

    this.applyDelta(wonId, -current);
  }

  formatPunchRange(timelog: PunchVm): string {
    const inText = this.formatPunchDisplay(timelog.clockIn?.logTime, timelog.clockIn?.timeZoneId);
    const outText = timelog.clockOut
      ? this.formatPunchDisplay(timelog.clockOut.logTime, timelog.clockOut.timeZoneId)
      : 'Missing punch';

    return `${inText} - ${outText}`;
  }

  private observeWeekBarShell(shell: HTMLElement): void {
    if (this.observedWeekBarShell === shell) {
      this.syncWeekBarHeight();
      return;
    }

    this.weekBarResizeObserver?.disconnect();
    this.observedWeekBarShell = shell;

    this.syncWeekBarHeight();

    this.weekBarResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.syncWeekBarHeight());
    });

    this.weekBarResizeObserver.observe(shell);
  }

  private syncWeekBarHeight(): void {
    const shell = this.observedWeekBarShell;
    if (!shell) return;

    const height = Math.ceil(shell.getBoundingClientRect().height);

    document.documentElement.style.setProperty('--hours-week-bar-height', `${height}px`);
  }

  private updateViewportMode(): void {
    this.isMobileView = window.innerWidth <= this.mobileBreakpoint;
  }

  private loadInitial(): void {
    this.startLoading();
    this.errorMessage = null;

    this.vm$ = this.hoursService.getCurrentUser().pipe(
      tap((user) => {
        this.user = user;
      }),
      switchMap((user) => {
        const year = dayjs().year();

        return forkJoin({
          weeks: this.hoursService.getAllWeeksForYear(year, 50),
          wons: this.hoursService.getAllUserWons(user.id, 50),
        });
      }),
      tap(({ weeks, wons }) => {
        this.weeks = this.sortWeeksAsc(weeks);
        this.userWons = wons;

        if (!this.weeks.length) {
          throw new Error('No weeks returned from /api/yywws for the current year.');
        }

        this.currentWeekIndex = this.resolveInitialWeekIndex(this.weeks);
        this.selectedDate = this.getDefaultSelectedDate(this.weeks[this.currentWeekIndex]);
        this.syncWeekToUrl(this.weeks[this.currentWeekIndex]?.fullYearWeek, true);
      }),
      switchMap(() => this.loadWeekReport(this.weeks[this.currentWeekIndex].fullYearWeek)),
      map(() => this.buildVm()),
      catchError((err) => {
        this.errorMessage = typeof err?.message === 'string' ? err.message : 'Failed to load hours';

        return of(this.safeEmptyVm());
      }),
      finalize(() => this.stopLoading()),
    );
  }

  private reloadWeek(): void {
    const week = this.weeks[this.currentWeekIndex];

    if (!week?.fullYearWeek) {
      this.errorMessage = 'Invalid week selection.';
      this.vm$ = of(this.safeEmptyVm());
      this.stopLoading();
      return;
    }

    this.startLoading();
    this.errorMessage = null;

    this.vm$ = this.loadWeekReport(week.fullYearWeek).pipe(
      map(() => this.buildVm()),
      catchError(() => {
        this.errorMessage = 'Failed to load week';

        return of(this.safeEmptyVm());
      }),
      finalize(() => this.stopLoading()),
    );
  }

  private loadWeekReport(fullYearWeek: string): Observable<WeeklyReportResponse> {
    return this.hoursService.getWeeklyReport(fullYearWeek).pipe(
      tap((report) => {
        this.report = report;
        this.draft.clear();
        this.baseline = this.buildBaselineFromReport(report, this.user.id);
      }),
    );
  }

  private applyDelta(workOrderNumberId: number, delta: number): void {
    const date = this.selectedDate;

    if (!this.canEditWonDate(workOrderNumberId, date)) {
      this.errorMessage = this.getWonDateLockReason(workOrderNumberId, date);
      this.renderVm();
      return;
    }

    const key = this.cellKey(workOrderNumberId, date);
    const base = this.getEffectiveCell(workOrderNumberId, date);
    const next = this.round2(Math.max(0, base.hours + delta));

    if (!base.bookedHourId && base.hours === 0 && next === 0) return;

    this.draft.set(key, {
      workOrderNumberId,
      date,
      bookedHourId: base.bookedHourId,
      hours: next,
    });

    this.renderVm();
  }

  private extractApiError(err: unknown): string | null {
    const message =
      (err as any)?.error?.message ?? (err as any)?.error?.title ?? (err as any)?.message ?? null;

    return typeof message === 'string' ? message : null;
  }

  private confirmDiscardIfDirty(): boolean {
    return !this.isDirty || confirm('You have unsaved changes. Discard them and continue?');
  }

  private buildVm(): HoursVm {
    const week = this.weeks[this.currentWeekIndex];
    const schedules = this.getSchedulesForCurrentUser();
    const dailyScheduled = this.buildDailyScheduled(week, schedules);
    const days = this.buildDaysVm(week, this.selectedDate, dailyScheduled);
    const weeklyBookedTotal = this.round2(days.reduce((sum, day) => sum + (day.booked || 0), 0));
    const weeklyClockedTotal = this.round2(days.reduce((sum, day) => sum + (day.clocked || 0), 0));
    const weeklyScheduledTotal = this.round2(
      dailyScheduled.reduce((sum, schedule) => (schedule ? sum + this.computeDurationHours(schedule) : sum), 0),
    );
    const isPayrollSynced = this.isCurrentWeekPayrollSynced();

    return {
      user: this.user,
      week,
      days,
      wonCards: this.buildWonCardsVm(this.selectedDate),
      selectedDate: this.selectedDate,
      dirty: this.isDirty,
      schedules,
      dailyScheduled,
      weeklyBookedTotal,
      weeklyClockedTotal,
      weeklyScheduledTotal,
      isPayrollSynced,
      payrollLockedMessage: isPayrollSynced ? this.getPayrollLockedMessage() : null,
    };
  }

  private getSchedulesForCurrentUser(): WeeklyReportAssignedSchedule[] {
    const wrapper = this.report?.data?.userSchedules?.find((wrapper) => wrapper.user.id === this.user?.id);

    return wrapper?.user?.schedules ?? [];
  }

  private buildDailyScheduled(
    week: YywwDto,
    schedules: WeeklyReportAssignedSchedule[],
  ): (WeeklyReportAssignedSchedule | null)[] {
    const byDate = new Map(schedules.map((schedule) => [this.toDateKey(schedule.assignedDate), schedule]));

    return Array.from(
      { length: 7 },
      (_, index) => byDate.get(dayjs(week.startDate).add(index, 'day').format('YYYY-MM-DD')) ?? null,
    );
  }

  getDurationHours(schedule: WeeklyReportAssignedSchedule): {
    totalHours: number;
    breakHours: number;
  } {
    if (!schedule?.schedule?.localStartTime || !schedule?.schedule?.localEndTime) {
      return { totalHours: 0, breakHours: 0 };
    }

    const [startH, startM] = schedule.schedule.localStartTime.split(':');
    const [endH, endM] = schedule.schedule.localEndTime.split(':');
    const startHNum = Number(startH ?? 0);
    const startMNum = Number(startM ?? 0);
    const endHNum = Number(endH ?? 0);
    const endMNum = Number(endM ?? 0);

    if (
      !Number.isFinite(startHNum) ||
      !Number.isFinite(startMNum) ||
      !Number.isFinite(endHNum) ||
      !Number.isFinite(endMNum) ||
      startHNum < 0 ||
      startHNum > 23 ||
      startMNum < 0 ||
      startMNum > 59 ||
      endHNum < 0 ||
      endHNum > 23 ||
      endMNum < 0 ||
      endMNum > 59
    ) {
      return { totalHours: 0, breakHours: 0 };
    }

    let durationMinutes = endHNum * 60 + endMNum - (startHNum * 60 + startMNum);

    if (schedule.schedule.isOverNight || durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    const [breakH, breakM] = (schedule.schedule.breakTime ?? '00:00').split(':');
    let breakHNum = Number(breakH ?? 0);
    const breakMNum = Number(breakM ?? 0);

    if (!Number.isFinite(breakHNum) || !Number.isFinite(breakMNum)) {
      breakHNum = 0;
    }

    if (breakHNum === 12) {
      breakHNum = 0;
    }

    const breakMinutes = Math.max(0, breakHNum * 60 + breakMNum);
    const threshold = Number(schedule.schedule.breakThresholdHours ?? 0);
    const shouldSubtractBreak = threshold <= 0 || durationMinutes >= threshold * 60;

    const netMinutes = shouldSubtractBreak
      ? Math.max(0, durationMinutes - breakMinutes)
      : durationMinutes;

    return {
      totalHours: this.round2(netMinutes / 60),
      breakHours: shouldSubtractBreak ? this.round2(breakMinutes / 60) : 0,
    };
  }

  private computeDurationHours(schedule: WeeklyReportAssignedSchedule): number {
    return this.getDurationHours(schedule).totalHours;
  }

  private buildDaysVm(
    week: YywwDto,
    selected: IsoDate,
    dailyScheduled: (WeeklyReportAssignedSchedule | null)[],
  ): WeekDayVm[] {
    const start = dayjs(week.startDate);
    const today = dayjs().format('YYYY-MM-DD');

    return Array.from({ length: 7 }, (_, index) => {
      const day = start.add(index, 'day');
      const date = day.format('YYYY-MM-DD') as IsoDate;

      const booked = this.getBookedTotalForDateEffective(date);
      const clocked = this.hoursService.getClockedHoursForDate(this.report, this.user.id, date);
      const assignedSchedule = dailyScheduled[index];
      const scheduleBreakdown = assignedSchedule
        ? this.getDurationHours(assignedSchedule)
        : { totalHours: 0, breakHours: 0 };

      const scheduledStart = assignedSchedule?.schedule?.localStartTime
        ? this.formatScheduleLocalTime(assignedSchedule.schedule.localStartTime)
        : null;
      const scheduledEnd = assignedSchedule?.schedule?.localEndTime
        ? this.formatScheduleLocalTime(assignedSchedule.schedule.localEndTime)
        : null;
      const breakTime = scheduleBreakdown.breakHours;

      const userDay = this.report.data.timeLogs
        ?.find((row) => row.user?.id === this.user.id)
        ?.user.logs?.find((logDay) => this.toDateKey(logDay.date) === date);

      const timeLogs: PunchVm[] =
        userDay?.timeLogs.map((timeLog) => ({
          id: timeLog.id,
          hours: timeLog.hours,
          totalHours: timeLog.totalHours,
          clockIn: timeLog.clockIn
            ? {
                id: timeLog.clockIn.id,
                logTime: timeLog.clockIn.logTime,
                timeZoneId: timeLog.clockIn.timeZoneId,
              }
            : null,
          clockOut: timeLog.clockOut
            ? {
                id: timeLog.clockOut.id,
                logTime: timeLog.clockOut.logTime,
                timeZoneId: timeLog.clockOut.timeZoneId,
              }
            : null,
        })) ?? [];

      const isApproved = this.isDayFullyApproved(date);
      const isLocked = !this.canEditDate(date);
      const lockReason = this.getDateLockReason(date);
      const status = this.getDayStatus(date, booked, clocked, timeLogs);
      const punchesTooltip = this.buildPunchTooltipFromTimelogs(timeLogs);

      return {
        date,
        label: day.format('ddd'),
        booked,
        clocked,
        scheduledStart,
        scheduledEnd,
        breakTime,
        punchesTooltip,
        status,
        isToday: date === today,
        isSelected: date === selected,
        timelogs: timeLogs,
        isApproved,
        isLocked,
        lockReason,
      };
    });
  }

  private buildWonCardsVm(selectedDate: IsoDate): HoursWonCardVm[] {
    const uniqueWonIds = Array.from(new Set(this.userWons.map((item) => item.workOrderNumber.id)));

    return uniqueWonIds
      .map((wonId): HoursWonCardVm | null => {
        const won = this.userWons.find((item) => item.workOrderNumber.id === wonId)?.workOrderNumber;

        if (!won) return null;

        const weekTotal = this.getWonWeekTotalEffective(wonId);
        const dayCell = this.getEffectiveCell(wonId, selectedDate);

        const selectedDayIsApproved = this.isWorkOrderApprovedForDate(wonId, selectedDate);
        const selectedDayIsLocked = !this.canEditWonDate(wonId, selectedDate);
        const selectedDayLockReason = this.getWonDateLockReason(wonId, selectedDate);

        return {
          wonId,
          won: won.won,
          description: won.description ?? null,
          weekTotalBookedHours: weekTotal,
          selectedDayBookedHours: dayCell.hours,
          selectedDayBookedHourId: dayCell.bookedHourId,
          selectedDayIsApproved,
          selectedDayIsLocked,
          selectedDayLockReason,
        };
      })
      .filter((card): card is HoursWonCardVm => card !== null)
      .sort((a, b) => a.won.localeCompare(b.won));
  }

  private safeEmptyWeek(): YywwDto {
    const year = dayjs().year();
    const startDate = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
    const endDate = dayjs().endOf('isoWeek').format('YYYY-MM-DD');

    return {
      id: 0,
      yearWeek: '',
      fullYearWeek: '',
      year,
      week: 0,
      startDate,
      endDate,
    } as YywwDto;
  }

  private safeEmptyVm(): HoursVm {
    const selectedDate = dayjs().format('YYYY-MM-DD') as IsoDate;

    return {
      user: { id: 0, email: '', firstName: '', lastName: '' } as CurrentUserDto,
      week: this.safeEmptyWeek(),
      days: [],
      wonCards: [],
      selectedDate,
      dirty: false,
      schedules: [],
      dailyScheduled: new Array(7).fill(null),
      weeklyBookedTotal: 0,
      weeklyClockedTotal: 0,
      weeklyScheduledTotal: 0,
      isPayrollSynced: false,
      payrollLockedMessage: null,
    };
  }

  private getEffectiveCell(
    workOrderNumberId: number,
    date: IsoDate,
  ): { bookedHourId?: number; hours: number } {
    const key = this.cellKey(workOrderNumberId, date);
    const draftCell = this.draft.get(key);

    if (draftCell) {
      return {
        bookedHourId: draftCell.bookedHourId,
        hours: this.round2(draftCell.hours),
      };
    }

    const baseCell = this.baseline.get(key);

    if (baseCell) {
      return {
        bookedHourId: baseCell.bookedHourId,
        hours: this.round2(baseCell.hours),
      };
    }

    return { hours: 0 };
  }

  private getBookedTotalForDateEffective(date: IsoDate): number {
    const serverTotal = this.hoursService.getBookedHoursForDate(this.report, this.user.id, date);

    let delta = 0;

    for (const cell of this.draft.values()) {
      if (cell.date !== date) continue;

      const key = this.cellKey(cell.workOrderNumberId, cell.date);
      const baseHours = this.baseline.get(key)?.hours ?? 0;

      delta += (cell.hours ?? 0) - baseHours;
    }

    return this.round2(Math.max(0, serverTotal + delta));
  }

  private getWonWeekTotalEffective(workOrderNumberId: number): number {
    const serverTotal = this.hoursService.getWeeklyBookedTotalForWon(
      this.report,
      this.user.id,
      workOrderNumberId,
    );

    let delta = 0;

    for (const cell of this.draft.values()) {
      if (cell.workOrderNumberId !== workOrderNumberId) continue;

      const key = this.cellKey(cell.workOrderNumberId, cell.date);
      const baseHours = this.baseline.get(key)?.hours ?? 0;

      delta += (cell.hours ?? 0) - baseHours;
    }

    return this.round2(Math.max(0, serverTotal + delta));
  }

  private computeDirty(): boolean {
    for (const cell of this.draft.values()) {
      if (this.isCellChanged(cell)) return true;
    }

    return false;
  }

  private isCellChanged(cell: DraftCell): boolean {
    const key = this.cellKey(cell.workOrderNumberId, cell.date);
    const base = this.baseline.get(key);

    const baseHours = this.round2(base?.hours ?? 0);
    const nextHours = this.round2(cell.hours);

    return Math.abs(baseHours - nextHours) >= 0.0001;
  }

  private buildBaselineFromReport(
    report: WeeklyReportResponse,
    employeeId: number,
  ): Map<string, { bookedHourId?: number; hours: number }> {
    const baseline = new Map<string, { bookedHourId?: number; hours: number }>();

    const wrapper = report.data.bookedhours?.find((item) => item.user?.id === employeeId) ?? null;
    const days = wrapper?.user?.bookedHours ?? [];

    for (const day of days) {
      const date = this.toDateKey(day.date) as IsoDate;

      for (const bookedHour of this.getBookedEntriesForDay(day)) {
        const wonId = bookedHour.workOrderNumber?.id;

        if (!wonId) {
          continue;
        }

        baseline.set(this.cellKey(wonId, date), {
          bookedHourId: bookedHour.id,
          hours: this.round2(Number(bookedHour.hours) || 0),
        });
      }
    }

    return baseline;
  }

  private buildPunchTooltipFromTimelogs(timeLogs: PunchVm[]): string {
    return timeLogs
      .map((log) => {
        const lines: string[] = [];

        if (log.clockIn?.logTime) {
          lines.push(`In: ${this.formatPunchDisplay(log.clockIn.logTime, log.clockIn.timeZoneId)}`);
        }

        if (log.clockOut?.logTime) {
          lines.push(
            `Out: ${this.formatPunchDisplay(log.clockOut.logTime, log.clockOut.timeZoneId)}`,
          );
        } else {
          lines.push('Out: Missing punch');
        }

        return lines.join('\n');
      })
      .join('\n\n');
  }

  private formatScheduleLocalTime(value: string): string {
    const [hours = '00', minutes = '00'] = value.split(':');

    return `${hours}:${minutes}`;
  }

  private formatPunchDisplay(utcValue?: string | null, timeZoneId?: string | null): string {
    if (!utcValue) return 'N/A';

    const date = this.parseUtcDate(utcValue);
    if (!date) return 'N/A';

    const safeTimeZone = this.getSafeTimeZoneId(timeZoneId);

    const timeText = new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    return `${timeText} (${safeTimeZone})`;
  }

  private parseUtcDate(raw?: string | null): Date | null {
    if (!raw) return null;

    const normalized = /([zZ]|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`;
    const date = new Date(normalized);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getSafeTimeZoneId(timeZoneId?: string | null): string {
    const fallback = 'UTC';

    if (!timeZoneId) return fallback;

    try {
      Intl.DateTimeFormat(undefined, { timeZone: timeZoneId });
      return timeZoneId;
    } catch {
      return fallback;
    }
  }

  private sortWeeksAsc(weeks: YywwDto[]): YywwDto[] {
    return [...(weeks ?? [])].sort((a, b) => a.startDate.localeCompare(b.startDate));
  }

  private findWeekIndexForToday(weeks: YywwDto[]): number {
    const today = dayjs().format('YYYY-MM-DD');
    const index = weeks.findIndex((week) => today >= week.startDate && today <= week.endDate);

    return index >= 0 ? index : Math.max(0, weeks.length - 1);
  }

  private getDefaultSelectedDate(week: YywwDto): IsoDate {
    const today = dayjs().format('YYYY-MM-DD');

    return (today >= week.startDate && today <= week.endDate ? today : week.startDate) as IsoDate;
  }

  private getSelectedDayIndexInCurrentWeek(): number {
    const week = this.weeks[this.currentWeekIndex];
    if (!week) return 0;

    const start = dayjs(week.startDate);
    const diff = dayjs(this.selectedDate).diff(start, 'day');

    if (diff < 0) return 0;
    if (diff > 6) return 6;

    return diff;
  }

  private getDateForWeekDayIndex(week: YywwDto, dayIndex: number): IsoDate {
    return dayjs(week.startDate).add(dayIndex, 'day').format('YYYY-MM-DD') as IsoDate;
  }

  private cellKey(wonId: number, date: IsoDate): string {
    return `${wonId}|${date}`;
  }

  private round2(value: number): number {
    return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
  }

  private getSelectedDayVm(vm: HoursVm): WeekDayVm | null {
    return vm.days.find((day) => day.date === vm.selectedDate) ?? null;
  }

  private renderVm(): void {
    this.vm$ = of(this.buildVm());
    this.cdr.detectChanges();
  }

  private startLoading(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
  }

  private stopLoading(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  formatPunchTimeRange(timelog: PunchVm): string {
    const inText = this.formatPunchTimeOnly(timelog.clockIn?.logTime, timelog.clockIn?.timeZoneId);
    const outText = timelog.clockOut
      ? this.formatPunchTimeOnly(timelog.clockOut.logTime, timelog.clockOut.timeZoneId)
      : 'Missing punch';

    return `${inText} - ${outText}`;
  }

  getPunchTimeZoneLabel(timelog: PunchVm): string {
    return timelog.clockIn?.timeZoneId || timelog.clockOut?.timeZoneId || 'UTC';
  }

  private formatPunchTimeOnly(utcValue?: string | null, timeZoneId?: string | null): string {
    if (!utcValue) return 'N/A';

    const date = this.parseUtcDate(utcValue);
    if (!date) return 'N/A';

    const safeTimeZone = this.getSafeTimeZoneId(timeZoneId);

    return new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  private getDayBookedPayCodeClass(date: IsoDate): 'paycode-holiday' | 'requested-offtime' | null {
    const bookedWrapper = this.report?.data?.bookedhours?.find((item) => item.user?.id === this.user.id);
    const bookedDay = bookedWrapper?.user?.bookedHours?.find((day) => this.toDateKey(day.date) === date);

    const bookedItems = this.getBookedEntriesForDay(bookedDay).filter(
      (item) => (Number(item.hours) || 0) > 0,
    );

    if (!bookedItems.length) {
      return null;
    }

    const normalizedPayCodes = bookedItems
      .map((item) =>
        String(item.payCode?.type ?? item.payCode?.name ?? '')
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);

    if (!normalizedPayCodes.length) {
      return null;
    }

    if (normalizedPayCodes.some((value) => value.includes('holiday'))) {
      return 'paycode-holiday';
    }

    if (
      normalizedPayCodes.some(
        (value) =>
          value.includes('vac') ||
          value.includes('pto') ||
          value.includes('sick') ||
          value.includes('personal') ||
          value.includes('bereav') ||
          value.includes('jury') ||
          value.includes('unpaid') ||
          value.includes('off'),
      )
    ) {
      return 'requested-offtime';
    }

    return null;
  }

  private hasMissingPunch(timeLogs: PunchVm[]): boolean {
    return timeLogs.some((log) => !log.clockIn?.logTime || !log.clockOut?.logTime);
  }

  isWonLockedForSelectedDate(workOrderNumberId: number): boolean {
    return !this.canEditSelectedWon(workOrderNumberId);
  }

  getWonLockReasonForSelectedDate(workOrderNumberId: number): string | null {
    if (this.isCurrentWeekPayrollSynced()) {
      return this.getPayrollLockedMessage();
    }

    if (this.isWonLockedForSelectedDate(workOrderNumberId)) {
      return 'This work order has already been approved for the selected day.';
    }

    return null;
  }

  isWonApprovedForSelectedDate(workOrderNumberId: number): boolean {
    return this.isWorkOrderApprovedForDate(workOrderNumberId, this.selectedDate);
  }

  getWonSelectedDateLockReason(workOrderNumberId: number): string | null {
    return this.getWonDateLockReason(workOrderNumberId, this.selectedDate);
  }

  get canEditCurrentSelection(): boolean {
    return this.canEditSelectedDate();
  }

  get currentSelectionLockReason(): string | null {
    return this.getDateLockReason(this.selectedDate);
  }

  private resolveInitialWeekIndex(weeks: YywwDto[]): number {
    const requestedWeek = this.getRequestedFullYearWeekFromUrl();

    if (requestedWeek) {
      const requestedIndex = weeks.findIndex((week) => week.fullYearWeek === requestedWeek);

      if (requestedIndex >= 0) {
        return requestedIndex;
      }
    }

    return this.findWeekIndexForToday(weeks);
  }

  private getRequestedFullYearWeekFromUrl(): string | null {
    const week = String(this.route.snapshot.queryParamMap.get('week') ?? '').trim();
    const fullYearWeek = String(this.route.snapshot.queryParamMap.get('fullYearWeek') ?? '').trim();
    const value = week || fullYearWeek;

    return /^\d{6}$/.test(value) ? value : null;
  }

  private syncWeekToUrl(fullYearWeek: string | null | undefined, replaceUrl = false): void {
    const week = String(fullYearWeek ?? '').trim();

    if (!/^\d{6}$/.test(week)) {
      return;
    }

    const currentWeek = String(this.route.snapshot.queryParamMap.get('week') ?? '').trim();

    if (currentWeek === week) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { week },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private isCurrentWeekPayrollSynced(): boolean {
    return !!this.report?.data?.payableHoursSync?.id;
  }

  private getPayrollLockedMessage(): string {
    const sync = this.report?.data?.payableHoursSync;

    if (!sync?.id) {
      return '';
    }

    const createdBy = sync.createdBy;
    const createdByName = createdBy
      ? `${createdBy.firstName ?? ''} ${createdBy.lastName ?? ''}`.trim() ||
        createdBy.employeeNumber ||
        'Unknown user'
      : 'Unknown user';

    const createdAt = sync.createdAt
      ? dayjs(sync.createdAt).format('MMM D, YYYY h:mm A')
      : 'unknown time';

    return `Payroll sync has already been run for this week by ${createdByName} on ${createdAt}. This week is locked.`;
  }

  private canEditSelectedDate(): boolean {
    return this.canEditDate(this.selectedDate);
  }

  private canEditSelectedWon(workOrderNumberId: number): boolean {
    return this.canEditWonDate(workOrderNumberId, this.selectedDate);
  }

  private canEditDate(date: IsoDate | null | undefined): boolean {
    if (!date) {
      return false;
    }

    return !this.isCurrentWeekPayrollSynced();
  }

  private canEditWonDate(workOrderNumberId: number, date: IsoDate | null | undefined): boolean {
    if (!date) {
      return false;
    }

    if (this.isCurrentWeekPayrollSynced()) {
      return false;
    }

    if (this.isWorkOrderApprovedForDate(workOrderNumberId, date)) {
      return false;
    }

    return true;
  }

  private getDateLockReason(date: IsoDate | null | undefined): string | null {
    if (!date) {
      return 'Invalid day selection.';
    }

    if (this.isCurrentWeekPayrollSynced()) {
      return this.getPayrollLockedMessage();
    }

    return null;
  }

  private getWonDateLockReason(
    workOrderNumberId: number,
    date: IsoDate | null | undefined,
  ): string | null {
    if (!date) {
      return 'Invalid day selection.';
    }

    if (this.isCurrentWeekPayrollSynced()) {
      return this.getPayrollLockedMessage();
    }

    if (this.isWorkOrderApprovedForDate(workOrderNumberId, date)) {
      return 'This work order has already been approved for the selected day and can no longer be edited.';
    }

    return null;
  }

  private isDayFullyApproved(date: IsoDate): boolean {
    const items = this.getBookedHourItemsForDate(date);

    return items.length > 0 && items.every((item) => !!item.supervisorApprovedDate || !!item.approvedBySupervisor);
  }

  private isWorkOrderApprovedForDate(workOrderNumberId: number, date: IsoDate): boolean {
    const items = this.getBookedHourItemsForDate(date).filter(
      (item) => item.workOrderNumber?.id === workOrderNumberId,
    );

    return items.length > 0 && items.every((item) => !!item.supervisorApprovedDate || !!item.approvedBySupervisor);
  }

  private getBookedHourItemsForDate(date: IsoDate): WeeklyReportBookedHourItem[] {
    const wrapper = this.report?.data?.bookedhours?.find((item) => item.user?.id === this.user?.id);
    const day = wrapper?.user?.bookedHours?.find((item) => this.toDateKey(item.date) === date);

    return this.getBookedEntriesForDay(day).filter((item) => Number(item.hours ?? 0) > 0);
  }

  private getBookedEntriesForDay(
    day: WeeklyReportBookedHoursDay | null | undefined,
  ): WeeklyReportBookedHourItem[] {
    return day?.bookedHours ?? day?.bookedHour ?? [];
  }

  private toDateKey(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private getDayStatus(
    date: IsoDate,
    booked: number,
    clocked: number,
    timeLogs: PunchVm[],
  ): DayStatus {
    const hasAny = booked > 0 || clocked > 0 || timeLogs.length > 0;

    if (!hasAny) {
      return 'none';
    }

    if (this.isDayFullyApproved(date)) {
      return 'approved';
    }

    if (this.hasMissingPunch(timeLogs)) {
      return 'missing-punch';
    }

    const payCodeClass = this.getDayBookedPayCodeClass(date);

    if (payCodeClass === 'paycode-holiday') {
      return 'paycode-holiday';
    }

    if (payCodeClass === 'requested-offtime') {
      return 'requested-offtime';
    }

    const diff = this.round2(clocked - booked);

    if (Math.abs(diff) <= 0.01) {
      return 'match';
    }

    return diff > 0 ? 'over-booked' : 'under-booked';
  }
}